import type { FC } from 'react';
import { Link } from 'react-router';
import { Clock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { InquiryPublicDto } from '@paladin-monorepo/common';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { InquiryStatusBadge } from '@/components/StatusBadge';
import { inquiryDisplayCode } from '@/components/dashboard/inquirer/inquirer-dashboard-data';
import { InquiryOfferDeadlineDisplay } from '@/components/inquiries/InquiryOfferDeadlineDisplay';
import viewAllArrowIcon from '@/assets/dashboard/view-all-arrow.svg';
import {
  dashboardWidgetCardClassName,
  dashboardWidgetLinkClassName,
  dashboardWidgetTitleClassName,
} from '@/components/dashboard/dashboard-widget-styles';
import { getAppFormatLocale } from '@/utils/locale';

type InquirerActiveProjectsTableProps = {
  inquiries: InquiryPublicDto[];
};

function InquirerActiveProjectsMobileCards({
  inquiries,
  dash,
}: {
  inquiries: InquiryPublicDto[];
  dash: string;
}) {
  const { t } = useTranslation();

  return (
    <div className="space-y-3 md:hidden">
      {inquiries.map((inquiry) => {
        const impact = inquiry.category?.name?.trim();
        return (
          <div
            key={inquiry.id}
            className="rounded-sm border border-border-subtle bg-card px-4 py-4"
          >
            <Link
              to={`/inquiries/${inquiry.id}`}
              className="block text-body font-medium text-brand-secondary hover:underline line-clamp-2"
            >
              {inquiry.title}
            </Link>
            <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
              <span className="font-mono text-[11px] text-text-disabled tabular-nums">
                {inquiryDisplayCode(inquiry)}
              </span>
              {impact ? (
                <span className="rounded-full bg-tint-blue px-2 py-0.5 font-label text-brand-secondary">
                  {impact}
                </span>
              ) : null}
            </div>
            {inquiry.deadline ? (
              <div className="mt-3 flex items-center gap-1.5 text-body text-danger">
                <span>
                  {t('dashboard.inquirer.widgets.activeProjects.mobileMilestone', {
                    date: new Intl.DateTimeFormat(getAppFormatLocale(), {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                    }).format(new Date(inquiry.deadline)),
                  })}
                </span>
                <Clock className="size-3 shrink-0" aria-hidden />
              </div>
            ) : (
              <span className="mt-3 block text-body text-text-disabled">{dash}</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

export const InquirerActiveProjectsTable: FC<InquirerActiveProjectsTableProps> = ({
  inquiries,
}) => {
  const { t } = useTranslation();
  const dash = t('dashboard.inquirer.widgets.activeProjects.placeholder');

  return (
    <Card className={dashboardWidgetCardClassName}>
      <CardHeader className="flex flex-row items-start justify-between gap-2 space-y-0 px-5 py-5">
        <CardTitle className={dashboardWidgetTitleClassName}>
          {t('dashboard.inquirer.widgets.activeProjects.title')}
        </CardTitle>
        <Link to="/inquiries" className={dashboardWidgetLinkClassName}>
          {t('dashboard.inquirer.widgets.activeProjects.viewAll')}
          <span className="relative size-3.5 overflow-clip">
            <img src={viewAllArrowIcon} alt="" className="size-full" width={14} height={15} />
          </span>
        </Link>
      </CardHeader>
      <CardContent className="px-0 pb-2">
        {inquiries.length === 0 ? (
          <p className="px-5 py-6 text-sm text-muted-foreground">
            {t('dashboard.inquirer.widgets.activeProjects.empty')}
          </p>
        ) : (
          <>
            <InquirerActiveProjectsMobileCards inquiries={inquiries} dash={dash} />

            <Table containerClassName="hidden md:block">
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="px-5">
                    {t('dashboard.inquirer.widgets.activeProjects.columns.project')}
                  </TableHead>
                  <TableHead>
                    {t('dashboard.inquirer.widgets.activeProjects.columns.expert')}
                  </TableHead>
                  <TableHead>
                    {t('dashboard.inquirer.widgets.activeProjects.columns.status')}
                  </TableHead>
                  <TableHead>
                    {t('dashboard.inquirer.widgets.activeProjects.columns.psp')}
                  </TableHead>
                  <TableHead className="hidden md:table-cell">
                    {t('dashboard.inquirer.widgets.activeProjects.columns.impact')}
                  </TableHead>
                  <TableHead className="hidden lg:table-cell">
                    {t('dashboard.inquirer.widgets.activeProjects.columns.escrow')}
                  </TableHead>
                  <TableHead className="px-5">
                    {t('dashboard.inquirer.widgets.activeProjects.columns.nextMilestone')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {inquiries.map((inquiry) => {
                  const impact = inquiry.category?.name?.trim() || dash;
                  return (
                    <TableRow key={inquiry.id}>
                      <TableCell className="max-w-[16rem] px-5 align-top whitespace-normal">
                        <Link
                          to={`/inquiries/${inquiry.id}`}
                          className="block text-body font-medium text-brand-secondary hover:underline line-clamp-2"
                        >
                          {inquiry.title}
                        </Link>
                        <span className="mt-1 block font-mono text-[11px] text-text-subtle tabular-nums">
                          {inquiryDisplayCode(inquiry)}
                        </span>
                      </TableCell>
                      <TableCell className="text-text-disabled">{dash}</TableCell>
                      <TableCell>
                        <InquiryStatusBadge status={inquiry.status} />
                      </TableCell>
                      <TableCell className="text-text-disabled">{dash}</TableCell>
                      <TableCell className="hidden max-w-[10rem] whitespace-normal text-text-secondary md:table-cell">
                        {impact}
                      </TableCell>
                      <TableCell className="hidden text-text-disabled lg:table-cell">
                        {dash}
                      </TableCell>
                      <TableCell className="px-5 tabular-nums">
                        <InquiryOfferDeadlineDisplay
                          deadline={inquiry.deadline}
                          showIcon={false}
                          className="text-body text-danger"
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </>
        )}
      </CardContent>
    </Card>
  );
};
